import { fmtDateTime } from '../../../shared/format';
import { Card, Empty, ErrorBox, Loading, useAsync } from '../../../shared/ui';
import { api, tenantPath } from '../../api';
import { AuditTable } from '../../components/AuditTable';
import { JsonDetail } from '../../components/JsonDetail';
import { actionLabel, roleLabel } from '../../labels';
import type { ActivityEntry, AuditEntry } from '../../types';
import type { TenantTabProps } from '../TenantDetail';

export function ActivityTab({ slug }: TenantTabProps): JSX.Element {
  const activity = useAsync(() => api.get<{ entries: ActivityEntry[] }>(`${tenantPath(slug)}/activity`).then((r) => r.entries), [slug]);
  const audit = useAsync(
    () => api.get<{ entries: AuditEntry[] }>(`/admin/audit?slug=${encodeURIComponent(slug)}`).then((r) => r.entries),
    [slug],
  );

  return (
    <div className="stack lg">
      <Card
        bodyless
        title={
          <div>
            <h2>Actividad en el portal</h2>
            <p className="subtle">Lo que han hecho proveedores, compras y recepción. Las últimas 200 acciones.</p>
          </div>
        }
        actions={
          <button className="btn" onClick={() => void activity.reload()} disabled={activity.loading}>
            Actualizar
          </button>
        }
      >
        {activity.loading && !activity.data ? (
          <Loading />
        ) : activity.error ? (
          <div className="card-body">
            <ErrorBox error={activity.error} onRetry={() => void activity.reload()} />
          </div>
        ) : !activity.data?.length ? (
          <Empty icon="☰">
            <p>Aún no hay actividad.</p>
            <p className="subtle">Cuando los proveedores acepten pedidos o propongan fechas, quedará apuntado aquí.</p>
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Quién</th>
                  <th>Proveedor</th>
                  <th>Pedido</th>
                  <th>Acción</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {activity.data.map((a) => (
                  <tr key={a.id}>
                    <td className="nowrap num">{fmtDateTime(a.at)}</td>
                    <td>
                      <div className="break">{a.actor}</div>
                      {a.actorRole ? <div className="subtle">{roleLabel(a.actorRole)}</div> : null}
                    </td>
                    <td className="mono">{a.cardCode ?? '—'}</td>
                    <td className="mono nowrap">
                      {a.docEntry ?? '—'}
                      {a.lineNum !== null && a.lineNum !== undefined ? <span className="subtle"> · línea {a.lineNum}</span> : null}
                    </td>
                    <td className="nowrap" title={a.action}>
                      {actionLabel(a.action)}
                    </td>
                    <td className="detail-cell">
                      <JsonDetail value={a.detail} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        bodyless
        title={
          <div>
            <h2>Cambios desde el panel</h2>
            <p className="subtle">Lo que ECINSA ha tocado en este cliente: conexión, sociedades, usuarios, configuración.</p>
          </div>
        }
      >
        {audit.loading && !audit.data ? (
          <Loading />
        ) : audit.error ? (
          <div className="card-body">
            <ErrorBox error={audit.error} onRetry={() => void audit.reload()} />
          </div>
        ) : (
          <AuditTable entries={audit.data ?? []} />
        )}
      </Card>
    </div>
  );
}
